import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../utils/axios';

/**
 * usePredictiveFiltering - Hook for predicting the impact of filter selections
 * 
 * This hook proactively calculates which pill selections would result in
 * zero or very few results, allowing the UI to disable or warn about
 * those selections BEFORE the user clicks.
 * 
 * KAI Standard: Proactive guidance, prevent dead ends
 * ZIA Standard: Learning from patterns, anticipate user needs
 * 
 * @param {Array} filters - Current filter state
 * @param {string} mode - Search mode
 * @param {number} currentEstimate - Current result estimate
 * @returns {Object} - { pillImpact, isCalculating, getPillState, refreshPredictions }
 */
export default function usePredictiveFiltering(filters, mode, currentEstimate) {
  // Map of pillId -> { estimatedCount, state: 'safe' | 'warning' | 'unavailable' }
  const [pillImpact, setPillImpact] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Calculate predictions when filters change
  const refreshPredictions = useCallback(async () => {
    if (!filters || filters.length === 0) {
      setPillImpact({});
      return;
    }
    
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setIsCalculating(true);
    
    try {
      // Collect all suggested (not yet included) pills for prediction
      const suggestedPills = [];
      filters.forEach(filter => {
        filter.pills?.forEach(pill => {
          if (pill.status === 'suggested') {
            suggestedPills.push({
              filterId: filter.id,
              pillId: pill.id,
              label: pill.label,
              apiValue: pill.apiValue || pill.label,
            });
          }
        });
      });
      
      // If no suggested pills, nothing to predict
      if (suggestedPills.length === 0) {
        setPillImpact({});
        setIsCalculating(false);
        return;
      }
      
      // Request impact prediction from backend
      const response = await api.post('/api/ai/predict-pill-impact', {
        filters,
        mode,
        pillsToPredict: suggestedPills,
        currentEstimate,
      }, {
        signal: abortControllerRef.current.signal,
      });
      
      if (response.data?.success && response.data.predictions) {
        const newImpact = {};
        
        response.data.predictions.forEach(prediction => {
          const key = `${prediction.filterId}:${prediction.pillId}`;
          const estimate = prediction.estimatedCount;
          
          // Determine state based on estimated count
          let state = 'safe';
          if (estimate === 0) {
            state = 'unavailable';
          } else if (estimate < 5) {
            state = 'warning';
          } else if (estimate < currentEstimate * 0.1) {
            // If selecting this would reduce results by >90%, warn
            state = 'warning';
          }
          
          newImpact[key] = {
            estimatedCount: estimate,
            state,
            percentChange: currentEstimate > 0 
              ? Math.round((estimate - currentEstimate) / currentEstimate * 100)
              : 0,
          };
        });
        
        setPillImpact(newImpact);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.warn('Failed to predict pill impact:', error);
      // On error, allow all pills (graceful degradation)
      setPillImpact({});
    } finally {
      setIsCalculating(false);
    }
  }, [filters, mode, currentEstimate]);
  
  // Debounced refresh
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      refreshPredictions();
    }, 500);
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [refreshPredictions]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // Get the state for a specific pill
  const getPillState = useCallback((filterId, pillId) => {
    const key = `${filterId}:${pillId}`;
    return pillImpact[key] || { state: 'safe', estimatedCount: null };
  }, [pillImpact]);
  
  // Get all unavailable pills
  const getUnavailablePills = useCallback(() => {
    return Object.entries(pillImpact)
      .filter(([_, impact]) => impact.state === 'unavailable')
      .map(([key]) => key);
  }, [pillImpact]);
  
  // Get suggestions for expanding search when results are low
  const getExpansionSuggestions = useCallback(() => {
    if (currentEstimate === null || currentEstimate > 20) {
      return [];
    }
    
    const suggestions = [];
    
    // Find included pills that could be removed to expand results
    filters.forEach(filter => {
      filter.pills?.forEach(pill => {
        if (pill.status === 'included') {
          suggestions.push({
            type: 'remove',
            filterId: filter.id,
            pillId: pill.id,
            label: `Remove "${pill.label}"`,
            filterLabel: filter.label,
          });
        }
      });
    });
    
    // Limit to top 3 suggestions
    return suggestions.slice(0, 3);
  }, [filters, currentEstimate]);
  
  return {
    pillImpact,
    isCalculating,
    getPillState,
    getUnavailablePills,
    getExpansionSuggestions,
    refreshPredictions,
  };
}

/**
 * Client-side heuristic for quick predictions when backend is slow
 * This provides immediate feedback while waiting for accurate server response
 */
export function useQuickPrediction(filters, currentEstimate) {
  const [quickPredictions, setQuickPredictions] = useState({});
  
  useEffect(() => {
    if (!filters || currentEstimate === null) {
      setQuickPredictions({});
      return;
    }
    
    const predictions = {};
    
    // Count current included pills
    let includedCount = 0;
    filters.forEach(filter => {
      includedCount += filter.pills?.filter(p => p.status === 'included').length || 0;
    });
    
    // Heuristic: Each additional filter typically reduces results by ~30-60%
    const reductionFactor = 0.45;
    
    filters.forEach(filter => {
      filter.pills?.forEach(pill => {
        if (pill.status === 'suggested') {
          const key = `${filter.id}:${pill.id}`;
          
          // Estimate impact based on current count and reduction factor
          const estimatedAfterAdding = Math.floor(currentEstimate * reductionFactor);
          
          let state = 'safe';
          if (estimatedAfterAdding === 0 || (currentEstimate > 0 && estimatedAfterAdding === 0)) {
            state = 'warning'; // Not unavailable since this is just a heuristic
          } else if (estimatedAfterAdding < 5) {
            state = 'warning';
          }
          
          predictions[key] = {
            estimatedCount: estimatedAfterAdding,
            state,
            isHeuristic: true, // Flag that this is a quick estimate
          };
        }
      });
    });
    
    setQuickPredictions(predictions);
  }, [filters, currentEstimate]);
  
  return quickPredictions;
}
